import { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Input,
  SectionHeader,
  Dropdown,
  Tooltip,
  Toggle,
  Button,
  InputField,
} from '@ets/design-system';
import cn from 'classnames';

import styles from './ManualMetadataModal.module.scss';

type SelectOptionFormProps = {
  option: { fields: string[]; extra: { label: string; value: string }[] };
  optionIndex: number;
  canDeleteOption: boolean;
  removeOption: (optionIndex: number) => void;
  changeField: (optionIndex: number, fieldIndex: number, newValue: string) => void;
  removeField: (optionIndex: number, fieldIndex: number) => void;
  addField: (optionIndex: number) => void;
  addAdditionalInformation: (optionIndex: number) => void;
  removeAdditionalInformation: (optionIndex: number, extraIndex: number) => void;
  editAdditionalInformation: (
    optionIndex: number,
    extraIndex: number,
    extraField: 'label' | 'value',
    extraValue: string,
  ) => void;
};

const SelectOptionForm: FC<SelectOptionFormProps> = ({
  option,
  optionIndex,
  canDeleteOption,
  removeOption,
  changeField,
  removeField,
  addField,
  addAdditionalInformation,
  removeAdditionalInformation,
  editAdditionalInformation,
}) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [showExtra, setShowExtra] = useState(false);
  const [deleting, setDeleting] = useState(false);
  return (
    <div className={styles.container}>
      <div className={styles.option}>
        <div className={cn(styles.fields, { [styles.deleting]: deleting })}>
          {deleting ? (
            <>
              <div className={styles.deletingMessage}>
                <FormattedMessage id="DELETING_METADATA_SELECT_OPTION" />
              </div>
              <Button
                size="m"
                variant="standard"
                style={{ margin: '0 0 0 auto' }}
                onClick={() => setDeleting(false)}
                testId={`option-${optionIndex}-cancel-delete-option`}
              >
                <FormattedMessage id="global.cancel" />
              </Button>
              <Button
                size="m"
                style={{ margin: '0 1rem 0 0.5rem' }}
                variant="danger"
                onClick={() => {
                  removeOption(optionIndex);
                  setDeleting(false);
                }}
                testId={`option-${optionIndex}-confirm-delete-option`}
              >
                <FormattedMessage id="DELETE_OPTION" />
              </Button>
            </>
          ) : (
            option.fields.map((field, index) => {
              return (
                <div key={index} className={styles.field}>
                  <Input
                    size="m"
                    placeholder={intl.formatMessage({ id: 'INSERT_VALUE' })}
                    value={field}
                    suffix={option.fields.length !== 1 ? 'trash' : undefined}
                    onSuffixClick={() => removeField(optionIndex, index)}
                    width="100%"
                    onChange={(e) => {
                      changeField(optionIndex, index, e.target.value);
                    }}
                    testId={`option-${optionIndex}-field-${index}`}
                  />
                </div>
              );
            })
          )}
          <div className={styles.toggle}>
            <Tooltip
              testId={`option-${optionIndex}-extra-toggle`}
              content={intl.formatMessage({
                id: 'OPTION_DOES_NOT_HAVE_ANY_ADDITIONAL_INFORMATION',
              })}
              visible={option.extra.length <= 0 || !deleting}
            >
              <Toggle
                variant="link"
                size="m"
                iconProps={{ icon: showExtra ? 'chevron-up' : 'chevron-down' }}
                disabled={option.extra.length === 0 || deleting}
                onClick={() => setShowExtra(!showExtra)}
                testId={`option-${optionIndex}-extra`}
              />
            </Tooltip>
          </div>
        </div>
        <Dropdown.Root open={open} onOpenChange={() => setOpen(!open)}>
          <Dropdown.Trigger>
            <Toggle
              size="l"
              iconProps={{ icon: 'ellipsis' }}
              variant="link"
              testId={`option-${optionIndex}-more-options`}
              disabled={deleting}
            />
          </Dropdown.Trigger>
          <Dropdown.Content
            style={{ width: '31rem' }}
            testId={`option-${optionIndex}-more-options-dropdown`}
            container={
              (document.querySelector('[data-slot="modal-content"]') as HTMLElement) ??
              document.body
            }
            align="end"
          >
            <Tooltip
              testId={`option-${optionIndex}-add-field-dropdown-item`}
              placement="left"
              content={<FormattedMessage id="METADATA_MAXIMUM_NUMBER_OF_FIELDS" />}
              visible={option.fields.length >= 3}
            >
              <span>
                <Dropdown.Item
                  onClick={() => addField(optionIndex)}
                  disabled={option.fields.length >= 3}
                  testId={`option-${optionIndex}-add-new-field-dropdown-item`}
                >
                  <FormattedMessage id="ADD_NEW_FIELD" />
                </Dropdown.Item>
              </span>
            </Tooltip>
            <Dropdown.Item
              onClick={() => {
                addAdditionalInformation(optionIndex);
                setShowExtra(true);
              }}
              testId={`option-${optionIndex}-add-extra-dropdown-item`}
            >
              <FormattedMessage id="ADD_ADDITIONAL_INFORMATION" />
            </Dropdown.Item>
            <Tooltip
              visible={!canDeleteOption}
              content={<FormattedMessage id="METADATA_TYPE_REQUIRES_OPTION" />}
              testId={`option-${optionIndex}-delete-option-dropdown-item`}
              placement="left"
            >
              <span>
                <Dropdown.Item
                  onClick={() => {
                    setDeleting(true);
                    setShowExtra(false);
                  }}
                  testId={`option-${optionIndex}-delete-option-dropdown-item`}
                  disabled={!canDeleteOption}
                >
                  <FormattedMessage id="DELETE_OPTION" />
                </Dropdown.Item>
              </span>
            </Tooltip>
          </Dropdown.Content>
        </Dropdown.Root>
      </div>
      {showExtra && (
        <div className={styles.extras}>
          {option.extra.map(({ label, value }, index) => {
            return (
              <div key={index} className={styles.extra}>
                <SectionHeader
                  margin="0 0 2rem 0"
                  size="m"
                  testId={`option-${optionIndex}-additional-information-${index}`}
                  primaryAction={{
                    type: 'button',
                    variant: 'standard',
                    children: <FormattedMessage id="global.remove" />,
                    onClick: () => removeAdditionalInformation(optionIndex, index),
                  }}
                >
                  <FormattedMessage id="ADDITIONAL_INFORMATION_#" values={{ number: index + 1 }} />
                </SectionHeader>
                <div className={styles.information}>
                  <InputField
                    label={intl.formatMessage({ id: 'global.name' })}
                    style={{ width: '100%' }}
                    size="l"
                    testId={`option-${optionIndex}-extra-${index}-name-input`}
                  >
                    <Input
                      size="l"
                      placeholder={intl.formatMessage({ id: 'INSERT_ADDITIONAL_INFORMATION_NAME' })}
                      value={label}
                      onChange={(e) =>
                        editAdditionalInformation(optionIndex, index, 'label', e.target.value)
                      }
                      testId={`option-${optionIndex}-extra-${index}-name`}
                    />
                  </InputField>
                  <InputField
                    label={intl.formatMessage({ id: 'VALUE' })}
                    style={{ width: '100%' }}
                    size="l"
                    testId={`option-${optionIndex}-extra-${index}-value-input`}
                  >
                    <Input
                      size="l"
                      placeholder={intl.formatMessage({
                        id: 'INSERT_ADDITIONAL_INFORMATION_VALUE',
                      })}
                      value={value}
                      onChange={(e) =>
                        editAdditionalInformation(optionIndex, index, 'value', e.target.value)
                      }
                      testId={`option-${optionIndex}-extra-${index}-value`}
                    />
                  </InputField>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SelectOptionForm;
